import { useTheme } from '@mui/material';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { ContentBlockType, SpaceType } from '@onesource/schemas';
import { useGetLesson, useGetLessons, useGetProgress, useGetSpace } from '@onesource/services';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import DOMPurify from 'dompurify';
import { useEffect } from 'react';

import { LessonDetailView } from '#/features/community-hub/components/lesson-detail-view';

export const Route = createFileRoute('/_protected/community-hub/spaces/$id/lesson/')({
  component: SingleLessonRoute,
});

function SingleLessonRoute() {
  const { id: spaceId } = Route.useParams();
  const navigate = useNavigate();
  const theme = useTheme();

  const { data: space, isLoading: isLoadingSpace, error: spaceError } = useGetSpace(spaceId);
  const { data: lessons, isLoading: isLoadingLessons } = useGetLessons(spaceId);
  const lessonId = lessons?.[0]?.id;
  const { data: lesson, isLoading: isLoadingLesson } = useGetLesson(lessonId ?? '', { enabled: !!lessonId });
  const { data: progress } = useGetProgress(lessonId ?? '');

  const isWrongSpaceType = !!space && space.spaceType !== SpaceType.SINGLE_LESSON;

  useEffect(() => {
    if (isWrongSpaceType) {
      navigate({ to: `/community-hub/spaces/${spaceId}`, replace: true });
    }
  }, [isWrongSpaceType, spaceId, navigate]);

  if (isLoadingSpace || isLoadingLessons || (lessonId && isLoadingLesson) || isWrongSpaceType) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (spaceError) {
    return (
      <Box p={3}>
        <Alert severity="error">Failed to load space: {spaceError.message}</Alert>
      </Box>
    );
  }

  if (!lesson) {
    return (
      <Box p={3}>
        <Alert severity="info">
          This space has no lesson yet. An admin needs to publish the lesson from the dashboard.
        </Alert>
      </Box>
    );
  }

  const breadcrumbs = (
    <Breadcrumbs
      sx={{
        mb: 2,
        fontFamily: '"Figtree", sans-serif',
        fontSize: { xs: 14, md: 16 },
        color: theme.palette.grey[600],
        '& .MuiBreadcrumbs-separator': { mx: 0.6 },
      }}
    >
      <Typography component="span" sx={{ fontSize: { xs: 14, md: 16 }, fontWeight: 700 }}>
        <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(space?.name || lesson.title) }} />
      </Typography>
    </Breadcrumbs>
  );

  const contentPathByType: Record<string, string> = {
    [ContentBlockType.VIMEO]: 'video',
    [ContentBlockType.PDF]: 'pdf',
    [ContentBlockType.TEXT]: 'text',
    [ContentBlockType.QUIZ]: 'quiz',
    [ContentBlockType.EMBED]: 'embed',
  };

  return (
    <LessonDetailView
      lesson={lesson}
      progressPercent={progress?.progressPercent ?? 0}
      breadcrumbs={breadcrumbs}
      onNavigateToBlock={block => {
        const segment = contentPathByType[block.blockType];
        if (!segment) return;
        // Single-lesson spaces reuse the module-based content viewers by treating
        // the space id as both the outer space and the module.
        navigate({
          to: `/community-hub/spaces/${spaceId}/modules/${spaceId}/lessons/${lesson.id}/${segment}/${block.id}`,
        });
      }}
      onNavigateToSubLesson={subLessonId => {
        navigate({ to: `/community-hub/spaces/${spaceId}/modules/${spaceId}/lessons/${subLessonId}` });
      }}
    />
  );
}
